import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { useUpsertPlan, useTwentyIPackageTypes } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { PlanDto, TwentyIPackageTypeDto, UpsertPlanRequest } from "@/types/api";

interface PlanEditorDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initial?: PlanDto;       // omitted = create mode
}

const EMPTY: UpsertPlanRequest = {
  slug: "", name: "", plan_type: "email", description: "",
  currency: "USD",
  price_monthly: 0, price_quarterly: null, price_annually: null,
  twentyi_product_code: "", hostshop_product_reference: "", twentyi_package_type_id: "",
  mailboxes_allowed: 0, smtp_relays_allowed: 0, aliases_allowed: 0, forwarders_allowed: 0,
  storage_gb_allowed: 0, ssl_certs_allowed: 0, websites_allowed: 0,
  domain_capacity: 1, bulk_emails_allowed: 0, active: true
};

export function PlanEditorDialog({ open, onOpenChange, initial }: PlanEditorDialogProps) {
  const isEdit = Boolean(initial);
  const [form, setForm] = React.useState<UpsertPlanRequest>(EMPTY);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});

  const upsert = useUpsertPlan();
  const pkgTypes = useTwentyIPackageTypes();
  const [pkgTypeList, setPkgTypeList] = React.useState<TwentyIPackageTypeDto[] | null>(null);
  const { toast } = useToast();

  async function loadPackageTypes() {
    if (pkgTypes.isPending) return;
    try {
      const res = await pkgTypes.mutateAsync();
      setPkgTypeList(res.package_types);
      if (res.package_types.length === 0) {
        toast({
          title: "No package types found",
          description: "Your 20i account returned no package types. Create them in my.20i.com first.",
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't load from 20i",
        description: err instanceof ApiError ? err.message : "Check the 20i API key + that the API is running.",
        variant: "destructive"
      });
    }
  }

  // Seed the form whenever the dialog opens with a (different) plan.
  React.useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setForm(initial ? fromPlan(initial) : EMPTY);
  }, [open, initial]);

  function patch<K extends keyof UpsertPlanRequest>(key: K, value: UpsertPlanRequest[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    try {
      await upsert.mutateAsync(form);
      toast({ title: isEdit ? "Plan updated" : "Plan created", variant: "success" });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { errors?: Record<string, string[]>; message?: string };
        if (body.errors) setFieldErrors(body.errors);
        toast({ title: "Validation failed", description: body.message ?? err.message, variant: "destructive" });
      } else {
        toast({ title: "Save failed", description: err instanceof Error ? err.message : "Network error.", variant: "destructive" });
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${initial?.name}` : "New plan"}</DialogTitle>
          <DialogDescription>
            Snake_case fields stored verbatim. Slug must be lowercase + hyphens; can't be changed after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="slug" label="Slug" value={form.slug} onChange={v => patch("slug", v)} disabled={isEdit} errors={fieldErrors["Request.Slug"]} placeholder="email-growth" />
            <Field id="name" label="Name" value={form.name} onChange={v => patch("name", v)} errors={fieldErrors["Request.Name"]} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <SelectField id="plan_type" label="Plan type" value={form.plan_type}
              options={["email", "hosting", "portfolio"]}
              onChange={v => patch("plan_type", v as UpsertPlanRequest["plan_type"])}
              errors={fieldErrors["Request.PlanType"]} />
            <Field id="currency" label="Currency" value={form.currency} onChange={v => patch("currency", v)} maxLength={3} />
            <Field id="domain_capacity" type="number" label="Domain capacity" value={String(form.domain_capacity)} onChange={v => patch("domain_capacity", n(v))} />
          </div>

          <TextAreaField id="description" label="Description" value={form.description ?? ""} onChange={v => patch("description", v)} />

          <div className="grid gap-3 sm:grid-cols-3">
            <Field id="price_monthly"  type="number" label="Price monthly"  value={String(form.price_monthly)}        onChange={v => patch("price_monthly",   nd(v))} />
            <Field id="price_quarterly" type="number" label="Price quarterly (opt)" value={form.price_quarterly?.toString() ?? ""} onChange={v => patch("price_quarterly", v === "" ? null : nd(v))} />
            <Field id="price_annually"  type="number" label="Price annual (opt)"   value={form.price_annually?.toString() ?? ""}  onChange={v => patch("price_annually",  v === "" ? null : nd(v))} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="twentyi_product_code"       label="20i product code"        value={form.twentyi_product_code ?? ""}       onChange={v => patch("twentyi_product_code", v)} placeholder="custom:email_growth:" />
            <Field id="hostshop_product_reference" label="HostShop product ref"    value={form.hostshop_product_reference ?? ""} onChange={v => patch("hostshop_product_reference", v)} placeholder="283999-5-dfw" />
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field id="twentyi_package_type_id" label="20i package type ID (for addWeb provisioning)"
                  value={form.twentyi_package_type_id ?? ""}
                  onChange={v => patch("twentyi_package_type_id", v)}
                  placeholder="pick below, or paste an id" />
              </div>
              <Button type="button" variant="outline" onClick={loadPackageTypes} disabled={pkgTypes.isPending}>
                {pkgTypes.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Load from 20i
              </Button>
            </div>
            {pkgTypeList && pkgTypeList.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {pkgTypeList.map(pt => {
                  const selected = form.twentyi_package_type_id === pt.id;
                  return (
                    <button
                      key={pt.id}
                      type="button"
                      onClick={() => patch("twentyi_package_type_id", pt.id)}
                      className={`rounded-md border px-2.5 py-1 text-xs transition ${
                        selected ? "border-primary bg-brand-accent/10 text-primary" : "border-border hover:border-primary/50"
                      }`}
                      title={`id: ${pt.id}`}
                    >
                      {pt.label || pt.id}
                      {pt.platform ? <span className="ml-1 text-muted-foreground">({pt.platform})</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <fieldset className="space-y-3 rounded-lg border border-border p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Allowances</legend>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field id="mailboxes_allowed"   type="number" label="Mailboxes"    value={String(form.mailboxes_allowed)}   onChange={v => patch("mailboxes_allowed",   n(v))} />
              <Field id="bulk_emails_allowed" type="number" label="Bulk emails"  value={String(form.bulk_emails_allowed)} onChange={v => patch("bulk_emails_allowed", n(v))} />
              <Field id="aliases_allowed"     type="number" label="Aliases"      value={String(form.aliases_allowed)}     onChange={v => patch("aliases_allowed",     n(v))} hint="-1 = ∞" />
              <Field id="forwarders_allowed"  type="number" label="Forwarders"   value={String(form.forwarders_allowed)}  onChange={v => patch("forwarders_allowed",  n(v))} hint="-1 = ∞" />
              <Field id="storage_gb_allowed"  type="number" label="Storage GB"   value={String(form.storage_gb_allowed)}  onChange={v => patch("storage_gb_allowed",  nd(v))} />
              <Field id="websites_allowed"    type="number" label="Websites"     value={String(form.websites_allowed)}    onChange={v => patch("websites_allowed",    n(v))} />
              <Field id="ssl_certs_allowed"   type="number" label="SSL certs"    value={String(form.ssl_certs_allowed)}   onChange={v => patch("ssl_certs_allowed",   n(v))} />
              <Field id="smtp_relays_allowed" type="number" label="SMTP relays"  value={String(form.smtp_relays_allowed)} onChange={v => patch("smtp_relays_allowed", n(v))} />
            </div>
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => patch("active", e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            Active (shown on public /plans)
          </label>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── small field helpers ────────────────────────────────────
interface FieldProps {
  id: string; label: string; value: string; onChange: (next: string) => void;
  type?: string; disabled?: boolean; placeholder?: string; errors?: string[]; hint?: string; maxLength?: number;
}
function Field({ id, label, value, onChange, type, disabled, placeholder, errors, hint, maxLength }: FieldProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type ?? "text"} value={value} onChange={e => onChange(e.target.value)}
             disabled={disabled} placeholder={placeholder} maxLength={maxLength}
             aria-invalid={!!errors?.length} />
      {errors?.length ? <p className="text-xs text-destructive">{errors[0]}</p>
                      : hint ? <p className="text-[11px] text-muted-foreground">{hint}</p>
                              : null}
    </div>
  );
}

function SelectField({ id, label, value, options, onChange, errors }: {
  id: string; label: string; value: string; options: string[]; onChange: (v: string) => void; errors?: string[];
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-invalid={!!errors?.length}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {errors?.length ? <p className="text-xs text-destructive">{errors[0]}</p> : null}
    </div>
  );
}

function TextAreaField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <textarea id={id} rows={2} value={value} onChange={e => onChange(e.target.value)}
        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
    </div>
  );
}

const n  = (s: string) => { const v = parseInt(s, 10);   return Number.isFinite(v) ? v : 0; };
const nd = (s: string) => { const v = parseFloat(s);     return Number.isFinite(v) ? v : 0; };

function fromPlan(p: PlanDto): UpsertPlanRequest {
  return {
    slug: p.slug, name: p.name, plan_type: p.plan_type as UpsertPlanRequest["plan_type"],
    description: p.description ?? "", currency: p.currency,
    price_monthly: p.price_monthly, price_quarterly: p.price_quarterly, price_annually: p.price_annually,
    twentyi_product_code: p.twentyi_product_code ?? "",
    hostshop_product_reference: p.hostshop_product_reference ?? "",
    twentyi_package_type_id: p.twentyi_package_type_id ?? "",
    mailboxes_allowed: p.mailboxes_allowed, smtp_relays_allowed: p.smtp_relays_allowed,
    aliases_allowed: p.aliases_allowed,     forwarders_allowed: p.forwarders_allowed,
    storage_gb_allowed: p.storage_gb_allowed, ssl_certs_allowed: p.ssl_certs_allowed,
    websites_allowed: p.websites_allowed,    domain_capacity: p.domain_capacity,
    bulk_emails_allowed: p.bulk_emails_allowed, active: p.active
  };
}
